import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute } from '@angular/router';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { inject } from '@angular/core';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { InvoiceService } from '@app/core/services/models/invoice.service';
import { BaseUrlService } from '@app/core/services/base-url.service';
import { ToastService } from '@app/core/services/toast.service';
import { primengImports } from '@app/core/primeng';
import { apiErrorMessage } from '@app/core/utils/api-error';

@Component({
  selector: 'app-invoices-preview',
  standalone: true,
  imports: [
    CommonModule,
    PdfViewerModule,
    ...primengImports
  ],
  templateUrl: './invoices-preview.component.html',
  styleUrl: './invoices-preview.component.scss'
})
export class InvoicesPreviewComponent implements OnInit, OnDestroy {
  private baseUrl = inject(BaseUrlService);

  record: any = null;
  invoiceRef: string | null = null;
  pdfUrlRaw: string | null = null;
  pdfBlobUrl: string | null = null;
  invoiceUuid: string | null = null;

  loading = false;
  loadingPdf = false;
  hasOnlinePayment = false;
  paying = false;

  constructor(
    private route: ActivatedRoute,
    private invoiceService: InvoiceService,
    private toastService: ToastService,
    private http: HttpClient
  ) {}

  ngOnDestroy() {
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }
  }

  ngOnInit() {
    this.configurePdfWorker();
    this.invoiceUuid = this.route.snapshot.paramMap.get('uuid');
    this.notifyPaymentOutcome();
    if (this.invoiceUuid) {
      this.loadInvoice();
    } else {
      this.toastService.show('Facture introuvable', 'error');
    }
  }

  configurePdfWorker() {
    const pdfjsVersion = '4.8.69';
    const workerUrl = `https://cdn.jsdelivr.net/npm/pdfjs-dist@${pdfjsVersion}/legacy/build/pdf.worker.min.mjs`;
    if (typeof window !== 'undefined' && (window as any).pdfjsLib) {
      (window as any).pdfjsLib.GlobalWorkerOptions.workerSrc = workerUrl;
    }
  }

  loadInvoice() {
    if (!this.invoiceUuid) return;

    this.loading = true;
    this.invoiceService.getByUuid(this.invoiceUuid).subscribe({
      next: (invoice: any) => {
        this.record = invoice;
        this.invoiceRef = invoice.invoice_ref;
        this.hasOnlinePayment = invoice.has_online_payment === true;

        this.loading = false;

        // Le PDF est généré à la demande (pdf_url renseigné par generate-pdf/{id}).
        // S'il est absent, on le génère puis on l'affiche ; sinon on charge l'existant.
        this.preparePdf(invoice);
      },
      error: (err) => {
        console.error('Error loading invoice:', err);
        this.toastService.show('Erreur lors du chargement de la facture', 'error');
        this.loading = false;
      }
    });
  }

  /**
   * Prépare l'URL du PDF puis le charge. Si la facture n'a pas encore de pdf_url
   * (PDF jamais généré), on déclenche la génération côté API et on affiche le résultat.
   * Repli sur un éventuel pdf_url existant si la génération échoue.
   */
  private preparePdf(invoice: any): void {
    const show = (url: string | null | undefined): void => {
      if (!url) {
        this.toastService.show('PDF indisponible pour cette facture', 'error');
        return;
      }
      this.pdfUrlRaw = `${this.baseUrl.uploadPath}/pdf/${url}`;
      this.loadPdfAsBlob();
    };

    if (invoice?.pdf_url) {
      show(invoice.pdf_url);
      return;
    }
    // Page PUBLIQUE : le PDF est généré côté serveur par l'aperçu public
    // (getEntryForPreview). On n'appelle JAMAIS la route authentifiée generate-pdf
    // ici, sinon le client anonyme reçoit un 401 -> l'intercepteur le redirige vers
    // le formulaire de connexion. S'il manque encore, simple indisponibilité.
    show(null);
  }

  loadPdfAsBlob() {
    if (!this.pdfUrlRaw) return;

    this.loadingPdf = true;
    if (this.pdfBlobUrl) {
      URL.revokeObjectURL(this.pdfBlobUrl);
      this.pdfBlobUrl = null;
    }

    this.http.get(this.pdfUrlRaw, {
      responseType: 'blob',
      headers: new HttpHeaders({ 'Accept': 'application/pdf' })
    }).subscribe({
      next: (blob: Blob) => {
        this.pdfBlobUrl = URL.createObjectURL(blob);
        this.loadingPdf = false;
      },
      error: (err) => {
        console.error('Error loading PDF:', err);
        this.toastService.show('Erreur lors du chargement du PDF', 'error');
        this.loadingPdf = false;
      }
    });
  }

  downloadPDF() {
    if (!this.pdfUrlRaw) return;
    window.open(this.pdfUrlRaw, '_blank');
  }

  /** Lance le paiement en ligne (PayZen) : initie côté API puis redirige vers la passerelle. */
  payOnline(): void {
    if (!this.invoiceUuid || this.paying) return;
    this.paying = true;
    this.invoiceService.initiateOnlinePayment(this.invoiceUuid).subscribe({
      next: (res: any) => {
        if (res?.fields && res?.action_url) {
          // Soumet le formulaire → redirige le navigateur vers PayZen.
          this.invoiceService.submitPaymentForm(res.fields, res.action_url);
        } else {
          this.paying = false;
          this.toastService.show('Paiement en ligne indisponible pour cette facture', 'error');
        }
      },
      error: (err: any) => {
        this.paying = false;
        const msg = apiErrorMessage(err) || 'Erreur lors de l\'initiation du paiement';
        this.toastService.show(msg, 'error');
      }
    });
  }

  /** Retour utilisateur après redirection PayZen (?payment=success|cancelled|refused|error). */
  private notifyPaymentOutcome(): void {
    const outcome = this.route.snapshot.queryParamMap.get('payment');
    if (!outcome) return;
    switch (outcome) {
      case 'success':
        this.toastService.show('Paiement effectué avec succès', 'success');
        break;
      case 'cancelled':
        this.toastService.show('Paiement annulé', 'info');
        break;
      case 'refused':
        this.toastService.show('Paiement refusé', 'error');
        break;
      default:
        this.toastService.show('Le paiement n\'a pas abouti', 'error');
    }
  }
}
